import Emittery, { default as Emittery$1 } from "emittery";
import { v4 } from "uuid";
//#region src/event-bus.ts
var EventBus = class {
	emitter;
	config;
	subscriptions = /* @__PURE__ */ new Map();
	eventHistory = [];
	maxHistorySize = 1e3;
	constructor(config) {
		this.config = {
			maxListeners: 50,
			debug: false,
			recordHistory: true,
			logger: {
				debug: globalThis.console.debug,
				info: console.info,
				warn: console.warn,
				error: console.error
			},
			...config
		};
		this.emitter = new Emittery$1();
		if (this.config.debug) this.config.logger.debug(`[${this.config.name}] Initialized with config:`, this.config);
	}
	emit(eventName, eventData, source = "unknown") {
		const event = {
			id: v4(),
			timestamp: Date.now(),
			source,
			type: String(eventName),
			...eventData
		};
		if (this.config.debug) this.config.logger.debug(`[${this.config.name}] Emitting event: ${String(eventName)}`, event);
		if (this.config.recordHistory) this.addToHistory(event);
		return this.emitter.emit(eventName, event);
	}
	on(eventName, listener, options = {}) {
		const subscription = {
			eventName: String(eventName),
			isActive: true,
			listener,
			unsubscribe: () => {
				this.removeSubscription(eventName, subscription);
			}
		};
		const eventNameStr = String(eventName);
		if (!this.subscriptions.has(eventNameStr)) this.subscriptions.set(eventNameStr, /* @__PURE__ */ new Set());
		this.subscriptions.get(eventNameStr).add(subscription);
		const wrappedListener = this.createWrappedListener(listener, options, subscription);
		subscription.wrappedListener = wrappedListener;
		this.emitter.on(eventName, wrappedListener);
		if (this.config.debug) this.config.logger.debug(`[${this.config.name}] Subscribed to event: ${String(eventName)}`);
		return subscription;
	}
	once(eventName, listener, options = {}) {
		return this.on(eventName, listener, {
			...options,
			once: true
		});
	}
	off(eventName, listener) {
		this.unsubscribe(eventName, listener);
	}
	removeAllListeners(eventName) {
		if (eventName) {
			this.emitter.clearListeners(eventName);
			this.subscriptions.delete(eventName);
		} else {
			this.emitter.clearListeners();
			this.subscriptions.clear();
		}
		if (this.config.debug) this.config.logger.debug(`[${this.config.name}] Removed all listeners${eventName ? ` for: ${eventName}` : ""}`);
	}
	listenerCount(eventName) {
		return this.emitter.listenerCount(eventName);
	}
	eventNames() {
		return Array.from(this.subscriptions.keys());
	}
	getEventHistory(limit) {
		const events = [...this.eventHistory].reverse();
		return limit ? events.slice(0, limit) : events;
	}
	clearEventHistory() {
		this.eventHistory = [];
	}
	getStats() {
		const activeSubscriptions = Array.from(this.subscriptions.values()).reduce((total, set) => total + set.size, 0);
		return {
			totalEvents: this.eventHistory.length,
			activeSubscriptions,
			eventNames: this.eventNames(),
			historySize: this.eventHistory.length
		};
	}
	destroy() {
		this.removeAllListeners();
		this.clearEventHistory();
		this.subscriptions.clear();
	}
	unsubscribe(eventName, listener) {
		const eventNameStr = String(eventName);
		const eventSubscriptions = this.subscriptions.get(eventNameStr);
		if (eventSubscriptions) {
			let subscriptionToRemove;
			for (const subscription of eventSubscriptions) if (subscription.isActive && subscription.listener === listener) subscriptionToRemove = subscription;
			if (subscriptionToRemove) this.removeSubscription(eventName, subscriptionToRemove);
		}
		if (this.config.debug) this.config.logger.debug(`[${this.config.name}] Unsubscribed from event: ${String(eventName)}`);
	}
	removeSubscription(eventName, subscription) {
		if (!subscription.isActive) return;
		const eventNameStr = String(eventName);
		const eventSubscriptions = this.subscriptions.get(eventNameStr);
		subscription.isActive = false;
		if (subscription.wrappedListener) this.emitter.off(eventName, subscription.wrappedListener);
		eventSubscriptions?.delete(subscription);
		if (eventSubscriptions?.size === 0) this.subscriptions.delete(eventNameStr);
	}
	createWrappedListener(listener, options, subscription) {
		return async (event) => {
			if (!subscription.isActive) return;
			try {
				if (options.filter && !options.filter(event)) return;
				if (options.once) subscription.unsubscribe();
				await listener(event);
			} catch (error) {
				this.config.logger.error(`[${this.config.name}] Error in event listener for ${subscription.eventName}:`, error);
			}
		};
	}
	addToHistory(event) {
		this.eventHistory.push(event);
		if (this.eventHistory.length > this.maxHistorySize) this.eventHistory.shift();
	}
};
/**
* Creates a scoped event bus for specific use cases
* like modals manager in desktop and mobile app
*/
function createEventBus(config) {
	return new EventBus(config);
}
/**
* Global event bus instance
* This is the main instance that should be used throughout the application
*/
const globalEventBus = createEventBus({
	name: "GlobalEventBus",
	recordHistory: process.env.NODE_ENV !== "production"
});
//#endregion
//#region src/event-registry.ts
const EventNames = {
	AUTH_ERROR: "auth:error",
	AUTH_HTTP_ERROR: "auth:http_error",
	AUTH_LOGOUT: "auth:logout",
	AUTH_TOKEN_REFRESHED: "auth:token_refreshed",
	AUTH_DATA_SET: "auth:data_set",
	CHAT_MESSAGE: "chat:message",
	CHAT_DID_STOP: "chat:did_stop",
	CHAT_STREAMING_MESSAGE_DID_STOP: "chat:streaming_message_did_stop",
	CHAT_STREAMING_MESSAGE_WILL_STOP: "chat:streaming_message_will_stop",
	CHAT_ASSISTANT_DID_FINISH_MESSAGES: "chat:assistant_did_finish_messages",
	CHAT_ASSISTANT_DID_FINISH_STEP: "chat:assistant_did_finish_step",
	CHAT_NEW_ASSISTANT_THOUGHT: "chat:new_assistant_thought",
	CHAT_NEW_ASSISTANT_HIDDEN_TEXT: "chat:new_assistant_hidden_text",
	CHAT_ERROR: "chat:error_event",
	CHAT_SEND_FAILED: "chat:send_failed",
	PAYWALL_SHOW_BANNER: "paywall:show_banner",
	ANALYTICS_TRACK_MESSAGE_RESPONSE_EVENT: "analytics:track_event",
	ANALYTICS_TRACK_MESSAGE_ERROR_EVENT: "analytics:track_message_error",
	BOOTSTRAP_COMPLETE: "bootstrap:complete",
	CLIENT_TOOL_CALL_REQUEST: "chat:client_tool_call_request",
	CLIENT_TOOL_CONTENT_CHUNK: "chat:client_tool_content_chunk",
	CHAT_WAITING_FOR_CLIENT_TOOLS: "chat:waiting_for_client_tools",
	CHAT_WAITING_FOR_DEVICE_TOOLS: "chat:waiting_for_device_tools",
	CHAT_COMPACTION_STATUS: "chat:compaction_status",
	CHAT_TURN_CAP_HIT: "chat:turn_cap_hit",
	CHAT_IMAGE_GENERATION_QUOTA_EXCEEDED: "chat:image_generation_quota_exceeded",
	CHAT_COST_STEP: "chat:cost_step",
	ROUTINES_UPDATED: "routines:updated",
	BILLING_UPDATED: "billing:updated",
	WS_RECONNECTED: "ws:reconnected",
	PUSH_NOTIFICATION: "push_notification",
	ACTION_CARDS_CHANGED: "action_cards:changed",
	CHATS_CHANGED: "chats:changed",
	PROJECTS_CHANGED: "projects:changed",
	PEOPLE_WIKI_CHANGED: "people_wiki:changed",
	TEAM_CHATS_CHANGED: "team_chats:changed",
	MEETING_INTENT_DETECTED: "meeting_intent:detected"
};
//#endregion
export { Emittery, EventBus, EventNames, createEventBus, globalEventBus };

//# sourceMappingURL=index.js.map